import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OutcomePicker from "@/components/formative/OutcomePicker";
import OutcomeDetails from "@/components/formative/OutcomeDetails";
import FormativeNotes from "@/components/formative/FormativeNotes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FormativePanel = ({ subject, grade, studentName, profile }) => {
  const [ishodi, setIshodi] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [code, setCode] = useState(null);
  const [activity, setActivity] = useState("");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadingList(true);
    setCode(null);
    setNotes([]);
    setActivity("");
    axios
      .get(`${API}/ishodi`, { params: { subject, grade } })
      .then((res) => active && setIshodi(res.data?.ishodi || []))
      .catch(() => active && toast.error("Ishodi se nisu učitali."))
      .finally(() => active && setLoadingList(false));
    return () => {
      active = false;
    };
  }, [subject, grade]);

  const selected = ishodi.find((i) => i.code === code);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/formative`, {
        subject,
        grade,
        code,
        activity: activity.trim() || null,
        student_name: studentName || null,
        teacher_profile: profile,
      });
      setNotes(res.data?.notes || []);
      toast.success("Bilješke su generirane.");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Greška pri generiranju bilješki.");
    } finally {
      setLoading(false);
    }
  };

  const editNote = (level, text) =>
    setNotes((prev) => prev.map((n) => (n.level === level ? { ...n, text } : n)));

  return (
    <div className="space-y-10" data-testid="formative-panel">
      <section className="space-y-4">
        <div>
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-800">
            1. Odaberi odgojno-obrazovni ishod
          </h3>
          <p className="text-sm text-slate-500">
            Ishodi su preuzeti iz predmetnoga kurikuluma (Narodne novine, 2019).
          </p>
        </div>
        {loadingList ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="ishodi-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <OutcomePicker ishodi={ishodi} selectedCode={code} onSelect={(c) => { setCode(c); setNotes([]); }} />
        )}
      </section>

      {selected && (
        <section className="space-y-4" data-testid="formative-step-2">
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-800">
            2. Što konkretno vrednuješ? <span className="text-sm font-normal text-slate-400">(opcionalno)</span>
          </h3>
          <OutcomeDetails item={selected} />
          <div className="space-y-1.5">
            <Label htmlFor="activity" className="text-sm font-medium text-slate-700">Aktivnost ili tema</Label>
            <Input
              id="activity"
              value={activity}
              maxLength={300}
              onChange={(e) => setActivity(e.target.value)}
              placeholder="npr. lektira „Pinokio”, zbrajanje do 20 s prijelazom desetice…"
              data-testid="formative-activity-input"
              className="rounded-lg border-slate-200 bg-white"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="flex items-start gap-2 text-xs text-slate-500 max-w-xl">
              <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
              Brojevi 1–5 služe samo vama za orijentaciju. Prema čl. 2. Pravilnika o vrednovanju
              formativno vrednovanje ne rezultira ocjenom, pa bilješke ne sadrže ni ocjenu ni naziv razine.
            </p>
            <Button
              onClick={generate}
              disabled={loading}
              data-testid="formative-generate-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-5"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              {loading ? "Generiram…" : notes.length ? "Generiraj ponovno" : "Generiraj bilješke"}
            </Button>
          </div>
        </section>
      )}

      {notes.length > 0 && (
        <section className="space-y-4 pb-12" data-testid="formative-step-3">
          <div>
            <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-800">
              3. Kopiraj bilješku koja odgovara učeniku
            </h3>
            <p className="text-sm text-slate-500">Svaku bilješku možeš urediti prije kopiranja u e-Dnevnik.</p>
          </div>
          <FormativeNotes notes={notes} onChange={editNote} />
        </section>
      )}
    </div>
  );
};

export default FormativePanel;
